import React, { useEffect, useState, useContext } from 'react'
import { Link } from 'react-router-dom'
import { getCountdown } from '../helper/Countdown';
import publicApi from '../services/public.api';
import LocationContext from '../context/LocationContext';
import { useCart } from '../context/CartContext';
import { addToCartAnimated } from '../utils/addToCartAnimated';
import ProductCardQuantity, { getDefaultCardSelection, isProductHasVariants } from './ProductCardQuantity';
import { buildCartProductFromSelection } from '../utils/cartProductHelpers';

const BestSellsOne = () => {
    const [timeLeft, setTimeLeft] = useState(getCountdown());
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addedProductId, setAddedProductId] = useState(null);
    const [selections, setSelections] = useState({});
    const [weightHintId, setWeightHintId] = useState(null);

    // Cart context
    const { addToCart } = useCart();

    // Location-based pricing
    const context = useContext(LocationContext);
    const isCasaRegion = context?.isCasaRegion ?? false;

    // Get price based on location
    const getLocationPrice = (product) => {
        if (!product) return 0;
        const priceCasa = parseFloat(product.price_casa || 0);
        const regularPrice = parseFloat(product.price || 0);
        return (isCasaRegion && priceCasa > 0) ? priceCasa : regularPrice;
    };

    const getSelection = (product) =>
        selections[product.id] || getDefaultCardSelection(isProductHasVariants(product));

    const updateSelection = (product, next) => {
        setSelections((prev) => ({ ...prev, [product.id]: next }));
    };

    const handleAddToCart = (e, product) => {
        e.preventDefault();
        e.stopPropagation();
        let basePrice = getLocationPrice(product);
        if (product.discount > 0) {
            basePrice = basePrice * (1 - product.discount / 100);
        }
        const built = buildCartProductFromSelection(product, basePrice, getSelection(product));
        if (built.error) {
            setWeightHintId(product.id);
            setTimeout(() => setWeightHintId(null), 2000);
            return;
        }
        addToCartAnimated({
            event: e,
            product: built.product,
            addToCart,
            quantity: built.quantity,
        });
        setAddedProductId(product.id);
        setTimeout(() => setAddedProductId(null), 1500);
    };

    useEffect(() => {
        const interval = setInterval(() => {
            setTimeLeft(getCountdown());
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const fetchBestSellers = async () => {
            try {
                const response = await publicApi.getBestSellers();
                if (response.success && response.data) {
                    setProducts(response.data);
                }
            } catch (error) {
                console.error('Error fetching best sellers:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchBestSellers();
    }, []);

    // Get image URL
    const getImageUrl = (imagePath) => {
        return publicApi.getImageUrl(imagePath);
    };

    // Format price
    const formatPrice = (price) => {
        return parseFloat(price || 0).toFixed(2);
    };

    // Calculate sold percentage for progress bar
    const getSoldPercentage = (soldCount, initialStock) => {
        if (!initialStock || initialStock === 0) return 0;
        return Math.min(100, Math.round((soldCount / initialStock) * 100));
    };

    // Render product card
    const renderProductCard = (product, index) => {
        const soldPercentage = getSoldPercentage(product.sold_count, product.initial_stock);
        
        return (
            <div key={product.id} className="col-md-6">
                <div className="product-card style-two h-100 p-8 border border-gray-100 best-sells-product-hover rounded-16 position-relative transition-2 flex-align gap-16">
                    <div className="">
                        {product.discount > 0 && (
                            <span 
                                className="product-card__badge hot-deals-badge px-8 py-4 text-sm"
                                style={{ backgroundColor: '#eab802', color: '#000', width: '30%' }}
                            >
                                -{product.discount}%
                            </span>
                        )}
                        <Link
                            to={`/product-details/${product.id}`}
                            className="product-card__thumb flex-center"
                        >
                            <img 
                                src={getImageUrl(product.image)} 
                                alt={product.name}
                                style={{ maxHeight: '220px', maxWidth: '220px', objectFit: 'contain' }}
                                onError={(e) => {
                                    e.target.src = 'assets/images/thumbs/best-sell1.png';
                                }}
                            />
                        </Link>
                        <div className="countdown best-sells-countdown" id={`countdown${index}`}>
                            <ul className="countdown-list style-three flex-align flex-wrap">
                                <li className="countdown-list__item text-heading flex-align gap-4 text-sm fw-medium">
                                    <span className="hours" />
                                    {timeLeft.hours} Heures
                                </li>
                                <li className="countdown-list__item text-heading flex-align gap-4 text-sm fw-medium">
                                    <span className="minutes" />
                                    {timeLeft.minutes} Min
                                </li>
                                <li className="countdown-list__item text-heading flex-align gap-4 text-sm fw-medium">
                                    <span className="seconds" />
                                    {timeLeft.seconds} Sec
                                </li>
                            </ul>
                        </div>
                    </div>
                    <div className="product-card__content">
                        <div className="flex-between align-items-start gap-8 mb-12">
                            <div className="product-card__price">
                                {product.discount > 0 ? (
                                    <>
                                        {!isCasaRegion && (
                                            <span className="text-gray-400 text-md fw-semibold text-decoration-line-through">
                                                {formatPrice(getLocationPrice(product))} DH
                                            </span>
                                        )}
                                        <span className="text-heading text-md fw-semibold ">
                                            {formatPrice(getLocationPrice(product) * (1 - product.discount / 100))} DH <span className="text-gray-500 fw-normal">/Qty</span>
                                        </span>
                                    </>
                                ) : (
                                    <span className="text-heading text-md fw-semibold ">
                                        {formatPrice(getLocationPrice(product))} DH <span className="text-gray-500 fw-normal">/Qty</span>
                                    </span>
                                )}
                                {weightHintId === product.id && (
                                    <span className="text-danger text-xs fw-medium d-block mt-4">
                                        Choisissez un poids
                                    </span>
                                )}
                            </div>
                            <ProductCardQuantity
                                hasVariants={isProductHasVariants(product)}
                                selection={getSelection(product)}
                                onChange={(next) => updateSelection(product, next)}
                                collapsedUntilInteraction
                            />
                        </div>
                        <div className="flex-align gap-6">
                            <span className="text-xs fw-bold text-gray-600">
                                {parseFloat(product.rating_average || 0).toFixed(1)}
                            </span>
                            <span className="text-15 fw-bold text-warning-600 d-flex">
                                <i className="ph-fill ph-star" />
                            </span>
                            <span className="text-xs fw-bold text-gray-600">
                                ({product.rating_count || 0})
                            </span>
                        </div>
                        <h6 className="title text-lg fw-semibold mt-12 mb-8">
                            <Link to={`/product-details/${product.id}`} className="link text-line-2">
                                {product.name}
                            </Link>
                        </h6>
                        <div className="flex-align gap-4">
                            <span className="text-main-600 text-md d-flex">
                                <i className="ph-fill ph-storefront" />
                            </span>
                            <span className="text-gray-500 text-xs">
                                Groupe Délices Benkirane
                            </span>
                        </div>
                        <div className="mt-12">
                            <div
                                className="progress w-100 bg-color-three rounded-pill h-4"
                                role="progressbar"
                                aria-label="Sales progress"
                                aria-valuenow={soldPercentage}
                                aria-valuemin={0}
                                aria-valuemax={100}
                            >
                                <div
                                    className="progress-bar best-sells-progress rounded-pill"
                                    style={{ width: `${soldPercentage}%` }}
                                />
                            </div>
                            <span className="text-gray-900 text-xs fw-medium mt-8">
                                Vendu: {product.sold_count || 0}/{product.initial_stock || product.stock || 0}
                            </span>
                        </div>
                        <button
                            onClick={(e) => handleAddToCart(e, product)}
                            disabled={addedProductId === product.id}
                            className={`product-card__cart btn ${
                                addedProductId === product.id 
                                    ? 'btn-success' 
                                    : 'best-sells-btn'
                            } text-white py-11 px-24 rounded-pill flex-align gap-8 mt-24 w-100 justify-content-center`}
                            style={{
                                transition: 'all 0.3s ease',
                                border: 'none',
                                cursor: addedProductId === product.id ? 'default' : 'pointer'
                            }}
                        >
                            {addedProductId === product.id ? (
                                <>Ajouté <i className="ph ph-check-circle" /></>
                            ) : (
                                <>Ajouter au Panier <i className="ph ph-shopping-cart" /></>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <section className="best sells pb-80">
                <div className="container container-lg">
                    <div className="section-heading">
                        <h5 className="mb-0">Meilleures Ventes</h5>
                    </div>
                    <div className="text-center py-5">
                        <div className="spinner-border text-main" role="status">
                            <span className="visually-hidden">Chargement...</span>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="best sells pb-80">
            <div className="container container-lg">
                <div className="section-heading">
                    <div className="flex-between flex-wrap gap-8">
                        <h5 className="mb-0">Meilleures Ventes</h5>
                    </div>
                </div>
                <div className="row g-12">
                    <div className="col-xxl-8">
                        <div className="row gy-4">
                            {products.slice(0, 4).map((product, index) => renderProductCard(product, index))}
                        </div>
                    </div>
                    <div className="col-xxl-4">
                        <div className="position-relative rounded-16 bg-light-purple overflow-hidden p-28 z-1 text-center delivery-version-two">
                            <div className="">
                                <img
                                    src="assets/images/bg/special-snacks.png"
                                    alt=""
                                    className="position-absolute inset-block-start-0 inset-inline-start-0 z-n1 w-100 h-100 cover-img"
                                />
                                <div className="d-xxl-block d-none">
                                    <img 
                                        src="assets/images/thumbs/special-snacks-img.png" 
                                        alt="" 
                                        style={{
                                            width: '511px',
                                            height: 'auto',
                                            objectFit: 'contain',
                                            objectPosition: 'center'
                                        }}
                                    />
                                </div>
                                <div className="d-xxl-block d-none delivery-liner-col">
                                    <img src="assets/images/thumbs/liner.png" alt="" className="delivery-liner" />
                                </div>
                            </div>
                            <div className="py-xl-4">
                                <h4 className="mb-8 best-sells-khalie-title">Offre Spéciale Khalie</h4>
                                <div className="countdown my-32" id="countdown5">
                                    <ul className="countdown-list style-two flex-center flex-wrap">
                                        <li className="countdown-list__item text-heading flex-align gap-4 text-sm fw-medium colon-white">
                                            <span className="hours" />
                                            {timeLeft.hours} Heures
                                        </li>
                                        <li className="countdown-list__item text-heading flex-align gap-4 text-sm fw-medium colon-white">
                                            <span className="minutes" />
                                            {timeLeft.minutes} Min
                                        </li>
                                        <li className="countdown-list__item text-heading flex-align gap-4 text-sm fw-medium colon-white">
                                            <span className="seconds" />
                                            {timeLeft.seconds} Sec
                                        </li>
                                    </ul>
                                </div>
                                <Link
                                    to="/shop"
                                    className="mt-16 btn best-sells-khalie-btn fw-medium d-inline-flex align-items-center rounded-pill gap-8"
                                    tabIndex={0}
                                >
                                    Achetez maintenant
                                    <span className="icon text-xl d-flex">
                                        <i className="ph ph-arrow-right" />
                                    </span>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default BestSellsOne
